import { atoms } from 'website/src/lib/atoms'
import { Diagnostic, lintGutter, linter } from '@codemirror/lint'
import { syntaxTree } from '@codemirror/language'

import { useStore } from '@nanostores/react'

import { useRouter } from 'next/router'
import {
    generatePatchesForString,
    getMarkdown,
    mergeDocument,
    schemaGetter,
} from 'website/src/lib/editor-utils'
import {
    useEditorMode,
    useRootProps,
    useUpdateOnEditorTransaction,
} from 'website/src/lib/hooks'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useColorMode } from 'beskar/landing'

import { markdown } from '@codemirror/lang-markdown'
import { languages } from '@codemirror/language-data'
import { EditorView } from '@codemirror/view'
import { codemirrorExtensions } from 'website/src/components/editor/codemirror-setup'
import { toTipTap } from 'website/src/lib/markdown-to-prosemirror'
import { yDocToProsemirrorJSON, ySyncPluginKey } from 'y-prosemirror'
import { debounce, yDocToJson } from 'website/src/lib/utils'
import { ParseError } from '../../lib/errors'

const tiptapUserEvent = 'tiptap-sync'

const endContent = '\n'

function focusedOnMarkdownEditor() {
    return document.activeElement?.classList?.contains('cm-content')
}

export function MarkdownCodeEditor({}) {
    const tiptapEditor = useStore(atoms.editor)
    // useUpdateOnEditorTransaction(tiptapEditor)
    const router = useRouter()
    const { isDark } = useColorMode()
    const mode = useEditorMode()
    // const document = useStore(atoms.document)
    const elementRef = useRef<HTMLElement>()

    // TODO to edit the frontmatter i will add a save button that updates on db and reloads the page
    const {
        pageId,
        pageMarkdown,
        parseErrors: initialErrors,
        pageSlug,
    } = useRootProps()

    useLayoutEffect(() => {
        const yDoc = atoms.document.get()
        const element = elementRef.current
        if (!element) {
            console.error('no element')
            return
        }

        const md = (() => {
            if (!yDoc) {
                // yDoc is missing when the page has an error on github
                return pageMarkdown || ''
            }
            const md = getMarkdown(yDocToJson(yDoc), pageSlug) + endContent
            return md
        })()

        const onYUpdate = debounce((update, origin) => {
            // console.log('onYUpdate', origin)
            const focusedOnProseMirror =
                document.activeElement?.classList?.contains('ProseMirror')

            if (origin === ySyncPluginKey && focusedOnMarkdownEditor()) {
                console.log('ignoring self update')
                return
            }
            const oldCode = codeView.state.doc.toString()
            const json = yDocToJson(yDoc)

            // console.log('json', json)
            const proseMirrorDoc = schemaGetter(pageSlug).nodeFromJSON(json)

            // const { insertedText } = diffTipTapNodes(
            //     tiptapEditor.state.doc!,
            //     proseMirrorDoc,
            // )
            const md = getMarkdown(proseMirrorDoc, pageSlug) + endContent

            if (focusedOnMarkdownEditor()) {
                // use slow diffs things
                const changes = generatePatchesForString(oldCode, md)

                if (changes?.length) {
                    // console.log(changes)
                    codeView.dispatch({
                        changes,
                        // sequential: true,
                        userEvent: tiptapUserEvent,
                    })
                }
            } else {
                codeView.dispatch({
                    changes: [
                        {
                            insert: '',
                            from: 0,
                            to: oldCode.length,
                        },
                        {
                            insert: md,
                            from: 0,
                            to: oldCode.length,
                        },
                    ],
                    // sequential: true,
                    userEvent: tiptapUserEvent,
                })
            }

            // return
        }, 30)
        yDoc?.on('update', onYUpdate)

        const codeView = new EditorView({
            doc: md,
            parent: element,

            extensions: [
                ...codemirrorExtensions,
                lintGutter(),
                linter(function lintErrors(
                    view: EditorView,
                ): readonly Diagnostic[] {
                    const value = view.state.doc.toString()
                    let { error } = toTipTap({
                        str: value,
                        pageSlug,
                    })

                    if (error?.line == null) {
                        return []
                    }
                    const node = view.state.doc.line(error.line!)
                    return [
                        {
                            from: node.from,
                            to: node.to,
                            message: error.message,
                            severity: 'error' as 'error',
                        },
                    ]
                }),
                markdown({ codeLanguages: languages }),

                EditorView.updateListener.of(function onUpdate(vu) {
                    // console.log('focusedOnMarkdown', document.activeElement?.classList)
                    if (!focusedOnMarkdownEditor()) {
                        return
                    }
                    if (!vu.transactions?.length) {
                        return
                    }
                    const isMe = vu.transactions.some((x) =>
                        x.isUserEvent(tiptapUserEvent),
                    )
                    if (isMe) {
                        console.log('ignoring self update')
                        return
                    }

                    try {
                        const doc = vu.state.doc
                        const value = doc.toString()

                        // if (!value?.trim()) {
                        //     return
                        // }

                        let { node, error } = toTipTap({ str: value, pageSlug })

                        if (!node) {
                            return
                        }

                        try {
                            mergeDocument(atoms.document.get()!, node, pageSlug)
                        } catch (e) {
                            console.error(`Cannot merge document back`)
                        }
                    } catch (e) {
                        console.error(e)
                        console.error(
                            'error applying markdown changes back to tiptap',
                        )
                        // throw e
                    }
                }),
            ],
        })

        return () => {
            console.log('destroying codeView')
            codeView.destroy()
            yDoc?.off('update', onYUpdate)
            // tiptapEditor.off('update', onUpdate)
        }
    }, [tiptapEditor])
    const containerRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState('100%' as any)
    useLayoutEffect(() => {
        const onResize = () => {
            const element = containerRef.current
            if (!element) {
                return
            }
            element.style.width = '100%'
            const w = element.getBoundingClientRect().width
            setWidth(w)
        }
        onResize()
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [mode])

    return (
        <div
            ref={containerRef}
            style={{ width }}
            className='flex flex-col h-full overflow-x-auto grow'
        >
            <div
                className='markdown-editor grow not-prose h-full'
                ref={elementRef as any}
            ></div>
        </div>
    )
}
